import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { MagnifyingGlass, ArrowRight } from "@phosphor-icons/react";
import api from "../lib/api";
import { flag, fmtData, fmtEUR, countryName } from "../lib/format";
import Header from "../components/Header";
import Footer from "../components/Footer";

export default function Plans() {
  const { t, i18n } = useTranslation();
  const [params, setParams] = useSearchParams();
  const country = params.get("country") || "";
  const [q, setQ] = useState("");
  const [allPlans, setAllPlans] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  useEffect(() => {
    setLoading(true);
    Promise.all([api.get("/plans"), api.get("/plans/countries")])
      .then(([p, c]) => { setAllPlans(p.data); setCountries(c.data); })
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    let list = allPlans;
    if (country) list = list.filter((p) => p.country_iso2 === country);
    if (q) {
      const qq = q.toLowerCase();
      list = list.filter((p) => {
        const loc = countryName(p.country_iso2, i18n.language, p.country).toLowerCase();
        return p.country.toLowerCase().includes(qq) || loc.includes(qq) || p.name.toLowerCase().includes(qq);
      });
    }
    return list;
  }, [allPlans, country, q, i18n.language]);

  const grouped = useMemo(() => {
    const map = new Map();
    for (const p of filtered) {
      const key = p.country;
      if (!map.has(key)) map.set(key, { country: p.country, iso2: p.country_iso2, plans: [] });
      map.get(key).plans.push(p);
    }
    const lang = i18n.language;
    return Array.from(map.values()).sort((a, b) =>
      countryName(a.iso2, lang, a.country).localeCompare(countryName(b.iso2, lang, b.country), lang)
    );
  }, [filtered, i18n.language]);

  return (
    <div className="min-h-screen" data-testid="plans-page">
      <Header />

      {/* Search header */}
      <section className="relative overflow-hidden">
        <div className="absolute -top-10 right-0 w-72 h-72 bg-cobalt-100 blob animate-blob pointer-events-none" />
        <div className="max-w-7xl mx-auto px-5 lg:px-8 pt-14 pb-10 relative">
          <div className="overline mb-3">{t("common.destination")}</div>
          <h1 className="font-display font-bold text-4xl md:text-6xl tracking-tight">{t("plans.title")}</h1>
          <p className="mt-3 text-ink-700 max-w-xl text-lg">{t("plans.subtitle")}</p>

          <div className="mt-8 flex flex-wrap gap-3">
            <div className="flex items-center input-soft !py-0 !pl-4 !pr-0 flex-1 min-w-[280px] max-w-md bg-white">
              <MagnifyingGlass size={18} className="text-ink-400" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={t("common.searchPlaceholder")}
                className="flex-1 px-3 py-3 outline-none bg-transparent border-0"
                data-testid="plans-search-input"
              />
            </div>
            <select
              value={country}
              onChange={(e) => setParams(e.target.value ? { country: e.target.value } : {})}
              className="input-soft min-w-[220px] max-w-xs"
              data-testid="plans-country-filter"
            >
              <option value="">{t("plans.all")}</option>
              {countries.map((c) => (
                <option key={c.country_iso2} value={c.country_iso2}>
                  {flag(c.country_iso2)} {countryName(c.country_iso2, i18n.language, c.country)}
                </option>
              ))}
            </select>
          </div>
        </div>
      </section>

      <section className="pb-20">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 space-y-14">
          {loading && <div className="text-ink-500" data-testid="plans-loading">{t("common.loading")}</div>}
          {!loading && grouped.length === 0 && (
            <div className="card-soft p-10 text-center" data-testid="plans-empty">
              <div className="text-4xl mb-3">🔎</div>
              <p className="text-ink-700">{t("plans.empty")}</p>
            </div>
          )}
          {grouped.map((g) => (
            <div key={g.iso2} data-testid={`country-group-${g.iso2}`}>
              <div className="flex items-baseline gap-4 mb-5">
                <span className="text-4xl">{flag(g.iso2)}</span>
                <h2 className="font-display font-bold text-3xl md:text-4xl tracking-tight">{countryName(g.iso2, i18n.language, g.country)}</h2>
                <span className="pill bg-cobalt-50 text-cobalt-600">{g.plans.length} {t("common.plans")}</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {g.plans.map((p) => (
                  <div
                    key={p.id}
                    className="card-soft p-6 flex flex-col"
                    data-testid={`plan-card-${p.id}`}
                  >
                    <div className="flex items-start justify-between mb-5">
                      <div>
                        <div className="overline text-ink-500">{p.operator || "Yesim"}</div>
                        <div className="font-display font-semibold text-xl mt-1 tracking-tight">{p.name}</div>
                      </div>
                      <div className="text-right">
                        <div className="font-display font-bold text-2xl tracking-tight">{fmtEUR(p.retail_price)}</div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3 mb-5">
                      <div className="bg-cobalt-50 rounded-2xl p-3">
                        <div className="text-[10px] uppercase tracking-widest font-semibold text-cobalt-600">{t("common.data")}</div>
                        <div className="font-display font-bold text-lg mt-0.5">{fmtData(p.data_mb)}</div>
                      </div>
                      <div className="bg-coral-soft/60 rounded-2xl p-3">
                        <div className="text-[10px] uppercase tracking-widest font-semibold text-coral-deep">{t("common.days")}</div>
                        <div className="font-display font-bold text-lg mt-0.5">{p.days}</div>
                      </div>
                    </div>
                    <button
                      onClick={() => nav(`/checkout/${p.id}`)}
                      className="btn-primary mt-auto w-full"
                      data-testid={`plan-buy-${p.id}`}
                    >
                      {t("common.buy")} <ArrowRight size={16} weight="bold" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>
      <Footer />
    </div>
  );
}
